import React, { useState, useEffect } from 'react';
import { datasetService, Dataset } from '../services/datasetService';
import '../styles/dashboard-pages.css';

const DatasetPage: React.FC = () => {
  const [datasets, setDatasets] = useState<Dataset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadDatasets();
  }, []);

  const loadDatasets = async () => {
    setLoading(true);
    setError(null);
    try {
      const datasetList = await datasetService.listDatasets();
      console.log('[DatasetPage] Loaded datasets:', datasetList);
      setDatasets(datasetList);
    } catch (err: any) {
      const errorMsg = err.message || 'Failed to load datasets';
      setError(errorMsg);
      console.error('[DatasetPage] Error loading datasets:', err);
      alert(`Error loading datasets: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Unknown';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  const filteredDatasets = datasets.filter((dataset) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      dataset.name.toLowerCase().includes(query) ||
      (dataset.display_name && dataset.display_name.toLowerCase().includes(query)) ||
      (dataset.description && dataset.description.toLowerCase().includes(query))
    );
  });

  return (
    <div className="dashboard-page">
      <div className="page-header">
        <h1>Datasets</h1>
        <p>Pre-configured datasets ready for training</p>
      </div>

      <div className="page-actions">
        <button className="action-btn primary">
          + Create Dataset
        </button>
        <button className="action-btn secondary">
          📊 Import Dataset
        </button>
      </div>

      <div className="page-sections">
        <section className="section-card">
          <div className="section-header">
            <h2>Configured Datasets</h2>
            <input
              type="text"
              placeholder="Search datasets..."
              className="search-input"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {loading ? (
            <div className="empty-state">
              <div className="empty-icon">⏳</div>
              <p>Loading datasets...</p>
            </div>
          ) : error ? (
            <div className="empty-state">
              <div className="empty-icon">⚠️</div>
              <p>Error: {error}</p>
              <button className="action-btn secondary" onClick={loadDatasets} style={{ marginTop: '12px' }}>
                Retry
              </button>
            </div>
          ) : filteredDatasets.length > 0 ? (
            <div className="datasets-table">
              <div className="table-header">
                <div className="col-name">Dataset Name</div>
                <div className="col-type">Type</div>
                <div className="col-size">Samples</div>
                <div className="col-rows">Created</div>
                <div className="col-status">Status</div>
                <div className="col-actions">Actions</div>
              </div>

              {filteredDatasets.map((dataset) => (
                <div key={dataset.id} className="table-row">
                  <div className="col-name">{dataset.display_name || dataset.name}</div>
                  <div className="col-type">{dataset.dataset_type || 'N/A'}</div>
                  <div className="col-size">{dataset.total_samples ? dataset.total_samples.toLocaleString() : 'N/A'}</div>
                  <div className="col-rows">{formatDate(dataset.created_at)}</div>
                  <div className="col-status">
                    <span className="status-badge ready">Ready</span>
                  </div>
                  <div className="col-actions">
                    <button className="icon-btn" title="Use for Training">🚀</button>
                    <button className="icon-btn" title="Preview">👁️</button>
                    <button className="icon-btn" title="Download">📥</button>
                    <button className="icon-btn" title="Share">🔗</button>
                    <button 
                      className="icon-btn danger" 
                      title="Delete"
                      onClick={async () => {
                        if (window.confirm(`Delete ${dataset.display_name || dataset.name}?`)) {
                          try {
                            await datasetService.deleteDataset(dataset.id);
                            loadDatasets();
                          } catch (err: any) {
                            alert(`Failed to delete: ${err.message}`);
                          }
                        }
                      }}
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <div className="empty-icon">📊</div>
              <p>
                {searchQuery 
                  ? 'No datasets match your search.' 
                  : 'No datasets yet. Create your first dataset to get started.'}
              </p>
            </div>
          )}
        </section>

        <section className="section-card">
          <h2>Dataset Configuration</h2>
          <p style={{ color: '#9ca3af', marginBottom: '20px' }}>
            Datasets are pre-configured data collections that have been processed and validated for direct use in training. 
            They include proper formatting, labels, and metadata required by the platform.
          </p>
          
          <div className="processing-options">
            <div className="process-card">
              <div className="process-icon">✅</div>
              <div className="process-name">Validated</div>
              <div className="process-desc">Data format verified</div>
            </div>
            <div className="process-card">
              <div className="process-icon">🏷️</div>
              <div className="process-name">Labeled</div>
              <div className="process-desc">Labels and metadata included</div>
            </div>
            <div className="process-card">
              <div className="process-icon">📊</div>
              <div className="process-name">Structured</div>
              <div className="process-desc">Ready for model input</div>
            </div>
            <div className="process-card">
              <div className="process-icon">🔒</div>
              <div className="process-name">Compatible</div>
              <div className="process-desc">Platform compatible format</div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default DatasetPage;

